import _ from 'lodash';
import { useMemo } from 'react';
//MUI
import { Box, Collapse, Stack } from '@mui/material';
//constants
import { BankAccountRequestTableColumns } from 'constants/bank-account.constants';
import { Beneficiary, BeneficiaryRequestTableColumns } from 'constants/beneficiary.constants';
import { BalanceDepositType, BalanceRequestTableColumns, BalanceType } from 'constants/balance.constants';
import { columnType } from 'components/BasicTable/types';
// components
import { HeaderPage2 } from 'theme/elements/Headers';
import BasicTable from 'components/BasicTable';
// reducers
import { useAppSelector } from 'hooks/redux.hooks';
import { getBankListObj, getCountriesObj, getCurrenciesObj } from 'state/reducers/global.reducers';
import { getMessage, getOpenPendingRequests } from 'state/reducers/requests.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { RequestContactTableColumn, RequsetPaymentTableColumn } from 'constants/requests.constants';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { TableLink } from 'theme/elements/Text';
import { getBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { CustomAlert } from 'theme/elements/Alerts';
import { CenteredNarrowContent } from 'theme/elements/Content';
import CreateMessage from 'components/CreateMessage';
import { ListTables } from 'theme/elements/List';
import { getFormattedNumber } from 'utils';

export default function OpenPending() {
	const t = useAppSelector(selectTranslations);
	const countriesObj = useAppSelector(getCountriesObj);
	const currenciesObj = useAppSelector(getCurrenciesObj);
	const bankListObj = useAppSelector(getBankListObj);
	const openPendingRequests = useAppSelector(getOpenPendingRequests);
	const beneficiaries = _.keyBy(useAppSelector(getBeneficiaries), 'id');
	const userMessage = useAppSelector(getMessage);
	const navigate = useNavigate();
	const prefixFields = 'page.pending_requests.tab.open_pending.';
	const cOpenPendingRequests = useMemo(() => {
		const finalObj: {
			[key: string]: [{ [key: string]: string | number }] | [];
		} = {};
		_.keys(openPendingRequests).forEach((category) => {
			finalObj[category] = openPendingRequests[category].map((item) => {
				const obj = {};
				_.keys(item).forEach((key) => {
					switch (key) {
						case 'status':
							obj[key] = t(prefixFields + 'status.' + item[key] + '.text');
							break;
						case 'beneficiaryId':
							if (_.has(beneficiaries, item['beneficiaryId'])) {
								let bn: Beneficiary = _.get(beneficiaries, item['beneficiaryId']);
								obj['companyNickname'] = bn?.nickname;
								obj['companyCountry'] = _.get(countriesObj, bn?.countryId || '')?.name;
							}
							break;
						case 'createdBy':
							obj[key] = item[key]?.firstName + ' ' + item[key]?.lastName;
							break;
						case 'type':
							obj[key] = item[key];
							obj['paymentType'] = t(
								`page.beneficiary.profile.payment_type.${_.toLower(item[key])}.text`
							);

							const isTopping = item[key] === BalanceType.Topping;
							const isDigital = item.depositType === BalanceDepositType.Digital;

							let translationKey = '';

							if (isTopping) {
								translationKey = `page.dashboard.pending_balance.add_balance${
									isDigital ? '_digital' : ''
								}.text`;
							} else {
								translationKey = `page.dashboard.pending_balance.${_.snakeCase(item[key])}.text`;
							}

							obj['balanceType'] = t(translationKey);

							break;
						case 'country':
							obj[key] = countriesObj[item[key]]?.name;
							break;
						case 'currency':
							obj[key] = currenciesObj[item[key]]?.code;
							break;
						case 'amount':
							let symbol = _.has(item, 'currencyId')
								? currenciesObj[item['currencyId']]?.symbol
								: _.isEqual(category, 'balancesRequests')
								? currenciesObj['16']?.symbol
								: _.get(item, 'currency.symbol'); //balance can be only shekels
							obj[key] = symbol + getFormattedNumber(item[key]);
							break;
						case 'role':
							obj[key] = item[key];
							break;
						case 'permission':
							obj[key] = item[key];
							break;
						default:
							obj[key] = item[key];
					}
				});
				return obj;
			});
		});
		return finalObj;
	}, [openPendingRequests]);

	const openPendingRequestsCount = useMemo(() => {
		return _.flatten(_.values(openPendingRequests)).length;
	}, [openPendingRequests]);

	const balanceRequestsColumns = () => {
		let columns: columnType[] = [];
		_.keys(BalanceRequestTableColumns).forEach((element) => {
			let props: columnType = {
				fieldId: 'id',
				name: element,
				title:
					element === 'balanceType'
						? t('page.dashboard.pending_balance.type.text')
						: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
				type: BalanceRequestTableColumns[element],
			};
			columns.push(props);
		});
		return columns;
	};
	const bankAccountRequestsColumns = () => {
		let columns: columnType[] = [];
		_.keys(BankAccountRequestTableColumns).forEach((element) => {
			let props: columnType = {
				fieldId: 'id',
				name: element,
				title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
				type: BankAccountRequestTableColumns[element],
			};
			columns.push(props);
		});
		return columns;
	};

	const beneficiaryRequestsColumns = () => {
		let columns: columnType[] = [];
		_.keys(BeneficiaryRequestTableColumns).forEach((element) => {
			let props: columnType = {
				fieldId: 'id',
				name: element,
				title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
				type: BeneficiaryRequestTableColumns[element],
			};
			switch (element) {
				case 'referenceNumber':
					props.eventsHandle = {
						onClick: (event) => {
							event.stopPropagation();
							const target = event.currentTarget as HTMLElement;
							navigate(`/${pagesEnum.BENEFICIARY}/details/${target.id}`);
						},
					};
					break;
			}
			columns.push(props);
		});
		return columns;
	};

	const paymentRequestsColumns = () => {
		let columns: columnType[] = [];
		_.keys(RequsetPaymentTableColumn).forEach((element) => {
			let props: columnType = {
				fieldId: 'id',
				name: element,
				title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
				type: RequsetPaymentTableColumn[element],
			};

			switch (element) {
				case 'reference':
					props.title = t(prefixFields + 'column.' + _.snakeCase('referenceNumber') + '.text');
					props.eventsHandle = {
						onClick: (event) => {
							event.stopPropagation();
							const target = event.currentTarget as HTMLElement;
							navigate(`/${pagesEnum.REQUEST}/requestDetailsPY/${target.id}`);
						},
					};
					break;
				case 'companyNickname': {
					props.title = t(prefixFields + 'column.' + _.snakeCase('companyName') + '.text');
					break;
				}
				case 'companyCountry': {
					props.title = t(prefixFields + 'column.' + _.snakeCase('country') + '.text');
					break;
				}
			}
			columns.push(props);
		});
		return columns;
	};

	const contactRequestsColumns = () => {
		let columns: columnType[] = [];
		_.keys(RequestContactTableColumn).forEach((element) => {
			let props: columnType = {
				fieldId: 'id',
				name: element,
				title: t(
					prefixFields +
						'column.' +
						(_.isEqual(element, 'reference') ? 'reference_number' : _.snakeCase(element)) +
						'.text'
				),
				type: RequestContactTableColumn[element],
			};
			columns.push(props);
		});
		return columns;
	};

	const [
		showBalancePendingRequests,
		showBankAccountPendingRequests,
		showBeneficiaryPendingRequests,
		showPaymentPendingRequests,
		showContactPendingRequests,
	] = useMemo(() => {
		try {
			return [
				cOpenPendingRequests?.balancesRequests.length > 0 || cOpenPendingRequests?.withdrawal.length > 0,
				cOpenPendingRequests?.bankAccountsRequests.length > 0,
				cOpenPendingRequests?.beneficiariesRequests.length > 0,
				cOpenPendingRequests?.paymentRequests.length > 0,
				cOpenPendingRequests?.contactRequests.length > 0,
			];
		} catch (e) {
			return [false, false, false, false, false];
		}
	}, [cOpenPendingRequests]);
	return (
		<>
			<Collapse in={userMessage.exist}>
				<CenteredNarrowContent>
					<CustomAlert severity={userMessage.type} text={userMessage.text}></CustomAlert>
				</CenteredNarrowContent>
			</Collapse>
			{openPendingRequestsCount === 0 ? (
				<CreateMessage
					noAction
					title={t(prefixFields + 'empty.title')}
					text={t(prefixFields + 'empty.subtitle')}
					imageKey={`${prefixFields}empty.image`}
				/>
			) : (
				<ListTables spacing={4}>
					{showBalancePendingRequests && (
						<Stack spacing={1}>
							<HeaderPage2 title={t(prefixFields + 'balance.title')} />
							<BasicTable
								data={[...cOpenPendingRequests.balancesRequests, ...cOpenPendingRequests.withdrawal]}
								columns={balanceRequestsColumns()}
								defaultSortBy="date"
								noPagination
								noSlice
							/>
						</Stack>
					)}
					{showPaymentPendingRequests && (
						<Stack spacing={1}>
							<HeaderPage2 title={t(prefixFields + 'payment.title')} />
							<BasicTable
								data={cOpenPendingRequests.paymentRequests}
								columns={paymentRequestsColumns()}
								defaultSortBy="createdAt"
								rowsClass="hoverButton"
								noPagination
								noSlice
							/>
						</Stack>
					)}
					{showBeneficiaryPendingRequests && (
						<Stack spacing={1}>
							<HeaderPage2 title={t(prefixFields + 'beneficiary.title')} />
							<BasicTable
								data={cOpenPendingRequests.beneficiariesRequests}
								columns={beneficiaryRequestsColumns()}
								defaultSortBy="date"
								rowsClass="hoverButton"
								noPagination
								noSlice
							/>
						</Stack>
					)}
					{showBankAccountPendingRequests && (
						<Stack spacing={1}>
							<HeaderPage2 title={t(prefixFields + 'bankAccount.title')} />
							<BasicTable
								data={cOpenPendingRequests.bankAccountsRequests}
								columns={bankAccountRequestsColumns()}
								defaultSortBy="date"
								noPagination
								noSlice
							/>
						</Stack>
					)}
					{showContactPendingRequests && (
						<Stack spacing={1}>
							<HeaderPage2 title={t(prefixFields + 'contact.title')} />
							<BasicTable
								data={cOpenPendingRequests.contactRequests}
								columns={contactRequestsColumns()}
								defaultSortBy="date"
								noPagination
								noSlice
							/>
						</Stack>
					)}
				</ListTables>
			)}
		</>
	);
}
